import React from 'react';

/* ------------------------------------------------------------------
 * Icon imports (selective, optimised)
 * ---------------------------------------------------------------- */

import { Phone, Mail, MessageSquare, User } from 'lucide-react';
import {
    ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

/* ------------------------------------------------------------------
 * Types (exported)
 * ---------------------------------------------------------------- */

export type ContactCardIcon =
    | 'phone'
    | 'email'
    | 'chat'
    | 'person'
    | 'message';

export type ContactCardVariant =
    | 'clean'
    | 'soft'
    | 'outline';

export type ContactCTA = {
    label: string;
    href: string;
};

export type ContactCardProps = {
    title?: string;
    description?: string;
    cta: ContactCTA;
    icon?: ContactCardIcon;
    variant?: ContactCardVariant;
    dark?: boolean;
    'data-entity'?: string;
};

/* ------------------------------------------------------------------
 * Icon map
 * ---------------------------------------------------------------- */

const ICON_SIZE_CLASS = 'h-6 w-6';

const iconMap: Record<ContactCardIcon, React.ReactNode> = {
    phone: <Phone className={ICON_SIZE_CLASS} aria-hidden />,
    email: <Mail className={ICON_SIZE_CLASS} aria-hidden />,
    chat: <ChatBubbleLeftRightIcon className={ICON_SIZE_CLASS} aria-hidden />,
    person: <User className={ICON_SIZE_CLASS} aria-hidden />,
    message: <MessageSquare className={ICON_SIZE_CLASS} aria-hidden />,
};

/* ------------------------------------------------------------------
 * Variant tokens
 * ---------------------------------------------------------------- */

const variantClasses: Record<ContactCardVariant, string> = {
    clean: `
        border border-transparent
        bg-transparent
    `,
    soft: `
        border border-gray-200
        bg-white
        shadow-sm
    `,
    outline: `
        border border-gray-300
        bg-transparent
    `,
};

/* ------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const ContactCard = ({
                         title = 'Talk to a Developer',
                         description,
                         cta,
                         icon = 'person',
                         variant = 'clean',
                         dark = false,
                         'data-entity': dataEntity,
                     }: ContactCardProps) => {
    return (
        <section
            data-component="contact-card"
            data-entity={dataEntity}
            className={`
                rounded-lg
                p-4
                ${variantClasses[variant]}
                ${dark ? 'border-white/20' : ''}
            `}
        >
            <div className="flex items-start gap-3">
                <span
                    className={`
                        mt-1
                        ${dark ? 'text-white' : 'text-gray-700'}
                    `}
                >
                    {iconMap[icon]}
                </span>

                <div className="flex-1">
                    {!!title && (
                        <h6
                            className={`
                                text-lg font-semibold
                                ${dark ? 'text-white' : ''}
                            `}
                        >
                            {title}
                        </h6>
                    )}

                    {!!description && (
                        <span
                            className={`
                                mt-1 text-sm
                                ${dark ? 'text-gray-300' : 'text-gray-600'}
                            `}
                        >
                            {description}
                        </span>
                    )}

                    <a
                        href={cta.href}
                        className={`
                            inline-block
                            mt-3
                            underline
                            text-sm
                            font-medium
                            ${
                            dark
                                ? 'text-blue-300 hover:text-blue-200'
                                : 'text-blue-600 hover:text-blue-800'
                        }
                        `}
                    >
                        {cta.label}
                    </a>
                </div>
            </div>
        </section>
    );
};

export default ContactCard;
